import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ProductBuilderSelection } from "../../../../types/ProductBuilderTypes";
import { Box, useScreenClass } from "@vp/swan";
import { buildRangesType } from "../../../../utils/RangeAttributes";
import { showTextBoxSelector } from "../../../../utils/ProductBuilderHelper";
import { BffDataContext } from "../../../../contexts/BffDataContextProvider";
import { ProductContext } from "../../../../contexts/ProductContextProvider";
import { UploadCtaClickedEventName } from "../../../../plugins/Ctas/components/Ctas/UploadFlowCta";
import { getScreenClass } from "../../../../utils/screenClass";
import {
  ProductChoiceChangedEvent,
  ProductOption,
  ProductOptionChangedEvent,
  RangedAttributeValidationCode,
} from "@vp/product-options-ui";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";
import PageConfig from "../../../../utils/PageConfig";
import { mapMpvValueToProductOptionRangedChoice } from "../../utils/mapMpvValueToProductOptionRangedChoice";
import "../../styles/Selector.scss";

type TextBoxSelectorProps = {
  selectionData: ProductBuilderSelection;
  disabled: boolean;
  onChange: (value: string | null) => void;
};

const TextBoxSelector = (props: TextBoxSelectorProps) => {
  const { selectionData, disabled, onChange } = props;
  const { tenant } = PageConfig;
  const { locale } = useContext(PageStaticDataContext);
  const { compatibleRanges, prices } = useContext(BffDataContext);
  const { selections = {}, clientSideStateInitialized } = useContext(ProductContext);
  const { isMobile } = getScreenClass(useScreenClass());
  const propertyKey = selectionData.property.key;
  const compatibleRangesForCurrentProperty = compatibleRanges?.[propertyKey] ?? [];
  const [showFormErrors, setShowFormErrors] = useState(false);

  const [lastPlayedEvent, setLastPlayedEvent] = useState<ProductChoiceChangedEvent>({
    choiceKey: "",
    code: RangedAttributeValidationCode.EMPTY_INPUT,
  });
  const textbox = useRef<HTMLDivElement>(null);

  const rangeInformation = buildRangesType(compatibleRangesForCurrentProperty);

  const showTextBox = showTextBoxSelector(
    compatibleRangesForCurrentProperty,
    clientSideStateInitialized
  );

  const scrollToTextBoxError = () => {
    textbox.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleProductChoiceChangedEvent = useCallback(
    (event: ProductOptionChangedEvent) => {
      if (!showTextBox || !rangeInformation) {
        return;
      }

      const { choiceKey } = event;

      if (lastPlayedEvent.choiceKey !== choiceKey) {
        onChange(choiceKey);
        setShowFormErrors(true);
        setLastPlayedEvent(event);
      }
    },
    [
      compatibleRangesForCurrentProperty,
      clientSideStateInitialized,
      rangeInformation,
      showTextBoxSelector,
      onChange,
      setLastPlayedEvent,
    ]
  );

  useEffect(() => {
    const handleEvent = () => {
      setShowFormErrors(true);
      if (lastPlayedEvent.code !== RangedAttributeValidationCode.SUCCESS) {
        scrollToTextBoxError();
      }
    };

    if (showTextBox) {
      document.addEventListener(UploadCtaClickedEventName, handleEvent);
    } else {
      document.removeEventListener(UploadCtaClickedEventName, handleEvent);
    }

    return () => {
      document.removeEventListener(UploadCtaClickedEventName, handleEvent);
    };
  }, [lastPlayedEvent, isMobile, UploadCtaClickedEventName, showTextBox]);

  if (
    !clientSideStateInitialized ||
    compatibleRangesForCurrentProperty.length === 0 ||
    !showTextBox
  ) {
    return <></>;
  }

  const unitOfMeasure = compatibleRangesForCurrentProperty?.[0]?.unitOfMeasure ?? "";

  const currency = Object.values(prices)?.[0]?.currency ?? "";
  const fractionDigits = Object.values(prices)?.[0]?.fractionDigits ?? 2;

  const productOption = {
    key: propertyKey,
    name: selectionData.property.name,
    choices: rangeInformation ? mapMpvValueToProductOptionRangedChoice(rangeInformation) : [],
    style: "textbox" as any,
    isRequired: true,
    selectedKey: selections[propertyKey],
    unitOfMeasure,
  };

  return (
    <Box ref={textbox} className="text-box-selector" mb={5}>
      <ProductOption
        productOption={productOption}
        tenant={tenant}
        currency={currency}
        fractionDigits={fractionDigits}
        locale={locale}
        priceDisplayMode="none"
        showIncompatibleChoices={false}
        skin={"tight"}
        hideImages={true}
        hideDescriptions={true}
        showFormErrors={showFormErrors}
        onChanged={handleProductChoiceChangedEvent}
      />
    </Box>
  );
};

export default TextBoxSelector;
